import { useCallback, useRef } from 'react';
import { LayoutDashboard, X } from 'lucide-react';

import { PanelBadge } from './agent-panel/PanelBadge';
import { PanelButton } from './agent-panel/PanelButton';
import { PanelCodeBlock } from './agent-panel/PanelCodeBlock';
import { PanelDataTable } from './agent-panel/PanelDataTable';
import { PanelKPICard } from './agent-panel/PanelKPICard';
import { PanelMarkdown } from './agent-panel/PanelMarkdown';
import { PanelProgressBar } from './agent-panel/PanelProgressBar';
import { AgentPanelTab } from './AgentPanelTab';

import type { A2UIComponentType } from '@shiftagent/shared';
import type { UseAgentPanelsReturn } from '../hooks/use-agent-panels';

const COMPONENT_RENDERERS: Record<
  A2UIComponentType,
  React.ComponentType<{ props: Record<string, unknown> }>
> = {
  Markdown: PanelMarkdown as any,
  DataTable: PanelDataTable as any,
  CodeBlock: PanelCodeBlock as any,
  KPICard: PanelKPICard as any,
  Button: PanelButton as any,
  Badge: PanelBadge as any,
  ProgressBar: PanelProgressBar as any,
};

const MIN_WIDTH = 200;
const MAX_WIDTH = 600;

export interface AgentPanelProps {
  hook: UseAgentPanelsReturn;
  onResize?: ((width: number) => void) | undefined;
  currentWidth?: number | undefined;
}

export function AgentPanel({ hook, onResize, currentWidth }: AgentPanelProps) {
  const { tabs, activeTabId, setActiveTab, closePanel } = hook;
  const activeTab = tabs.find((t) => t.tab_id === activeTabId) ?? tabs[0] ?? null;
  const dragStartX = useRef<number | null>(null);
  const dragStartWidth = useRef<number>(currentWidth ?? 280);

  const handleDragMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragStartX.current = e.clientX;
    dragStartWidth.current = currentWidth ?? 280;

    const onMouseMove = (ev: MouseEvent) => {
      if (dragStartX.current === null) return;
      const delta = dragStartX.current - ev.clientX;
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, dragStartWidth.current + delta));
      onResize?.(newWidth);
    };

    const onMouseUp = () => {
      dragStartX.current = null;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [currentWidth, onResize]);

  return (
    <div className="relative flex flex-col h-full border-l border-sd-border/20 bg-sd-background shadow-[-2px_0_8px_rgba(0,0,0,0.04)]">
      {/* Drag handle on left border */}
      <div
        role="separator"
        aria-label="Resize agent panel"
        aria-orientation="vertical"
        onMouseDown={handleDragMouseDown}
        className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize z-10 hover:bg-sd-warning/40 active:bg-sd-warning/60 transition-colors duration-150"
      />

      {/* Amber accent edge — agent identity carry-through */}
      <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-sd-warning/20 pointer-events-none" />

      {/* Header */}
      <div className="flex items-center justify-between px-sd-3 py-sd-2 border-b border-sd-border/20">
        <span className="text-sd-xs font-semibold uppercase tracking-widest text-sd-text-muted select-none pl-sd-2">
          Agent Panel
        </span>
        <button
          type="button"
          aria-label="Close agent panel"
          onClick={closePanel}
          className="text-sd-text-muted hover:text-sd-text hover:bg-sd-surface-elevated rounded-sd-sm p-1 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sd-primary/50"
        >
          <X size={14} aria-hidden="true" />
        </button>
      </div>

      {/* Tab strip — only when there are tabs */}
      {tabs.length > 0 && (
        <AgentPanelTab tabs={tabs} activeTabId={activeTabId} onSelect={setActiveTab} />
      )}

      {/* Content */}
      {activeTab ? (
        <div className="flex-1 overflow-y-auto min-h-0 p-sd-3 space-y-sd-3">
          {activeTab.components.map((component) => {
            const Renderer = COMPONENT_RENDERERS[component.type];
            if (!Renderer) {
              return (
                <div key={component.id} className="text-sd-xs text-sd-text-muted">
                  <span className="inline-flex items-center px-1.5 py-0.5 bg-sd-border/20 text-sd-text-muted text-sd-xs rounded-sd-sm border border-sd-border/30">
                    Unsupported component: {component.type}
                  </span>
                </div>
              );
            }
            return <Renderer key={component.id} props={component.props} />;
          })}
        </div>
      ) : (
        /* Empty state */
        <div className="flex-1 flex flex-col items-center justify-center gap-sd-3 text-center p-sd-4">
          <LayoutDashboard size={28} className="text-sd-warning/30" strokeWidth={1.5} aria-hidden="true" />
          <p className="text-sd-xs text-sd-text-muted leading-relaxed max-w-[160px]">
            Your agent's canvas will appear here
          </p>
        </div>
      )}
    </div>
  );
}
